import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useOrgMembership } from '../../hooks/useOrgMembership';
import Navbar from '../../components/common/Navbar';
import RoundBackButton from '../../components/common/RoundBackButton';
import { COLORS, FONTS, SHADOWS } from '../../styles/tokens';

export default function OrgPage() {
  const { userDoc } = useAuth();
  const { org, orgIsAdmin, orgIsOwner, membership } = useOrgMembership();
  const navigate = useNavigate();

  const orgId = userDoc?.orgId;

  const [members,       setMembers]       = useState([]);
  const [invites,       setInvites]       = useState([]);
  const [memberNames,   setMemberNames]   = useState({}); // { uid: displayName }
  const [loading,       setLoading]       = useState(true);
  const [error,    setError]    = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole,  setInviteRole]  = useState('member');
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState(null); // { action, label, onConfirm }

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }

    let membersResolved = true; // admin-only; if not admin, we skip
    let invitesResolved = !orgIsAdmin; // non-admins don't load invites

    function checkDone() {
      if (membersResolved && invitesResolved) setLoading(false);
    }

    const unsubMembers = onSnapshot(
      collection(db, 'organizations', orgId, 'members'),
      (snap) => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMembers(docs);
        membersResolved = true;
        checkDone();
      },
      () => { membersResolved = true; checkDone(); }
    );

    let unsubInvites = () => {};
    if (orgIsAdmin) {
      unsubInvites = onSnapshot(
        collection(db, 'organizations', orgId, 'invites'),
        (snap) => {
          const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setInvites(docs);
          invitesResolved = true;
          checkDone();
        },
        () => { invitesResolved = true; checkDone(); }
      );
    }

    return () => { unsubMembers(); unsubInvites(); };
  }, [orgId, orgIsAdmin]);

  // Fetch display names from publicProfiles when members list changes
  useEffect(() => {
    if (members.length === 0) return;
    let cancelled = false;
    // Use document ID (m.id) — it IS the uid and is always defined
    const uids = members.map(m => m.id).filter(Boolean);
    if (uids.length === 0) return;
    Promise.all(
      uids.map(uid =>
        getDoc(doc(db, 'publicProfiles', uid))
          .then(snap => snap.exists() ? [uid, snap.data().name || uid] : [uid, uid])
          .catch(() => [uid, uid])
      )
    ).then(entries => {
      if (!cancelled) setMemberNames(Object.fromEntries(entries));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [members]);

  const activeMembers  = members.filter(m => m.status === 'active');
  const pendingMembers = members.filter(m => m.status === 'pending');

  async function callFn(name, data) {
    const functions = getFunctions(undefined, 'europe-west1');
    return httpsCallable(functions, name)(data);
  }

  async function handleApprove(targetUid) {
    setActionLoading(true);
    setError('');
    try {
      await callFn('approveOrgMember', { orgId, targetUid });
    } catch (err) {
      setError(err.message ?? 'Godkjenning feilet');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject(targetUid) {
    setActionLoading(true);
    setError('');
    try {
      await callFn('rejectOrgMember', { orgId, targetUid });
    } catch (err) {
      setError(err.message ?? 'Avvisning feilet');
    } finally {
      setActionLoading(false);
      setConfirmDialog(null);
    }
  }

  async function handleRemove(targetUid) {
    setActionLoading(true);
    setError('');
    try {
      await callFn('removeOrgMember', { orgId, targetUid });
    } catch (err) {
      setError(err.message ?? 'Fjerning feilet');
    } finally {
      setActionLoading(false);
      setConfirmDialog(null);
    }
  }

  async function handleLeave() {
    setActionLoading(true);
    setError('');
    try {
      await callFn('leaveOrg', { orgId });
      navigate('/orderer');
    } catch (err) {
      setError(err.message ?? 'Kunne ikke forlate teamet');
    } finally {
      setActionLoading(false);
      setConfirmDialog(null);
    }
  }

  async function handleDeleteInvite(inviteId) {
    // Invites can only be deleted via admin SDK — for now show info
    // TODO: add deleteOrgInvite Cloud Function in future iteration
    setError('Sletting av invitasjoner kommer i neste versjon.');
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!inviteEmail.trim()) { setInviteError('E-post er påkrevd'); return; }
    setInviteLoading(true);
    setInviteError('');
    try {
      await callFn('inviteOrgMember', { orgId, email: inviteEmail.trim().toLowerCase(), role: inviteRole });
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteForm(false);
    } catch (err) {
      setInviteError(err.message ?? 'Invitasjon feilet');
    } finally {
      setInviteLoading(false);
    }
  }

  if (!orgId) {
    return (
      <div className="orderer-ui" style={styles.root}>
        <Navbar />
        <div style={styles.page}>
          <RoundBackButton onClick={() => navigate('/orderer/profil')} label="Tilbake til profil" />
          <div style={styles.card}>
            <p style={styles.empty}>Du er ikke tilknyttet noen organisasjon.</p>
            <button className="tippi-btn" style={styles.btn} onClick={() => navigate('/orderer/org/setup')}>
              Sett opp team
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="orderer-ui" style={styles.root}>
        <Navbar />
        <div style={styles.page}>
          <RoundBackButton onClick={() => navigate('/orderer/profil')} label="Tilbake til profil" />
          <p style={{ textAlign: 'center', color: COLORS.textLight ?? '#888', padding: 40 }}>Laster…</p>
        </div>
      </div>
    );
  }

  if (orgId && !loading && !orgIsAdmin) {
    // Active non-admin member — show limited self-service view
    if (membership?.status === 'active') {
      return (
        <div className="orderer-ui" style={styles.root}>
          <Navbar />
          <div style={styles.page}>
          <RoundBackButton onClick={() => navigate('/orderer/profil')} label="Tilbake til profil" />
            <div style={styles.card}>
              <h2 style={styles.title}>{org?.orgName ?? 'Team'}</h2>
              <p style={styles.subtitle}>Org.nr. {org?.orgNumber}</p>
              {error && <div style={styles.error}>{error}</div>}
              <button
                className="tippi-btn-secondary"
                style={styles.leaveBtn}
                onClick={() => setConfirmDialog({
                  label: 'Forlat teamet?',
                  onConfirm: handleLeave,
                })}
                disabled={actionLoading}
              >
                Forlat team
              </button>
            </div>
            {confirmDialog && (
              <div style={styles.overlay}>
                <div style={styles.dialog}>
                  <p style={styles.dialogLabel}>{confirmDialog.label}</p>
                  <div style={styles.dialogActions}>
                    <button
                      className="tippi-btn"
                      style={{ ...styles.btn, background: COLORS.danger ?? '#e74c3c' }}
                      onClick={confirmDialog.onConfirm}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Venter…' : 'Bekreft'}
                    </button>
                    <button
                      className="tippi-btn-secondary"
                      style={styles.btn}
                      onClick={() => setConfirmDialog(null)}
                      disabled={actionLoading}
                    >
                      Avbryt
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Pending or unknown state — no access
    return (
      <div className="orderer-ui" style={styles.root}>
        <Navbar />
        <div style={styles.page}>
          <RoundBackButton onClick={() => navigate('/orderer/profil')} label="Tilbake til profil" />
          <div style={styles.card}>
            <p style={styles.empty}>Du har ikke tilgang til denne siden.</p>
            <button className="tippi-btn" style={styles.btn} onClick={() => navigate('/orderer')}>
              Tilbake
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orderer-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>
          <RoundBackButton onClick={() => navigate('/orderer/profil')} label="Tilbake til profil" />

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{org?.orgName ?? 'Team'}</h2>
            <p style={styles.subtitle}>Org.nr. {org?.orgNumber}</p>
          </div>
          {!orgIsOwner && membership?.status === 'active' && (
            <button
              className="tippi-btn-secondary"
              style={styles.leaveBtn}
              onClick={() => setConfirmDialog({
                label: 'Forlat teamet?',
                onConfirm: handleLeave,
              })}
              disabled={actionLoading}
            >
              Forlat team
            </button>
          )}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Pending requests — admin only */}
        {orgIsAdmin && pendingMembers.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Ventende forespørsler ({pendingMembers.length})</h3>
            {pendingMembers.map(m => (
              <div key={m.id} style={styles.memberRow}>
                <div style={styles.memberInfo}>
                  <span style={styles.memberName}>{memberNames[m.id] ?? m.uid}</span>
                  <span style={styles.memberMeta}>Forespørsel om å bli med</span>
                </div>
                <div style={styles.memberActions}>
                  <button
                    className="tippi-btn"
                    style={styles.approveBtn}
                    onClick={() => handleApprove(m.uid)}
                    disabled={actionLoading}
                  >
                    Godkjenn
                  </button>
                  <button
                    className="tippi-btn-secondary"
                    style={styles.rejectBtn}
                    onClick={() => setConfirmDialog({ label: 'Avvis forespørsel?', onConfirm: () => handleReject(m.uid) })}
                    disabled={actionLoading}
                  >
                    Avvis
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Active members */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Aktive medlemmer ({activeMembers.length})</h3>
          {activeMembers.map(m => (
            <div key={m.id} style={styles.memberRow}>
              <div style={styles.memberInfo}>
                <span style={styles.memberName}>{memberNames[m.id] ?? m.uid}</span>
                <span style={{ ...styles.roleBadge, background: m.role === 'admin' ? COLORS.purple ?? '#7c3aed' : COLORS.blue ?? '#2563eb' }}>
                  {m.role === 'admin' ? 'Admin' : 'Medlem'}
                </span>
              </div>
              <div style={styles.memberMeta}>
                {m.joinedAt ? `Ble med ${m.joinedAt.toDate?.().toLocaleDateString('nb-NO')}` : ''}
              </div>
              {orgIsAdmin && m.uid !== org?.ownerUid && (
                <button
                  className="tippi-btn-secondary"
                  style={styles.removeBtn}
                  onClick={() => setConfirmDialog({ label: `Fjern ${memberNames[m.id] ?? m.uid} fra teamet?`, onConfirm: () => handleRemove(m.uid) })}
                  disabled={actionLoading}
                >
                  Fjern
                </button>
              )}
              {m.uid === org?.ownerUid && (
                <span style={styles.ownerBadge}>Eier</span>
              )}
            </div>
          ))}
          {activeMembers.length === 0 && <p style={styles.empty}>Ingen aktive medlemmer ennå.</p>}
        </div>

        {/* Pending invites — admin only */}
        {orgIsAdmin && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>Utestående invitasjoner ({invites.length})</h3>
            </div>
            {invites.map(inv => (
              <div key={inv.id} style={styles.memberRow}>
                <div style={styles.memberInfo}>
                  <span style={styles.memberName}>{inv.email}</span>
                  <span style={styles.memberMeta}>
                    Utløper {inv.expiresAt?.toDate?.().toLocaleDateString('nb-NO')}
                  </span>
                </div>
                <button
                  className="tippi-btn-secondary"
                  style={styles.removeBtn}
                  onClick={() => handleDeleteInvite(inv.id)}
                  disabled={actionLoading}
                >
                  Slett
                </button>
              </div>
            ))}
            {invites.length === 0 && <p style={styles.empty}>Ingen utestående invitasjoner.</p>}
          </div>
        )}

        {/* Invite form — admin only */}
        {orgIsAdmin && (
          <div style={styles.section}>
            {!showInviteForm ? (
              <button
                className="tippi-btn"
                style={styles.inviteOpenBtn}
                onClick={() => setShowInviteForm(true)}
              >
                + Inviter ny person
              </button>
            ) : (
              <form onSubmit={handleInvite} style={styles.inviteForm}>
                <h3 style={styles.sectionTitle}>Inviter til teamet</h3>
                {inviteError && <div style={styles.error}>{inviteError}</div>}
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>E-postadresse</label>
                  <input
                    className="tippi-input"
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="kollega@bedrift.no"
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Rolle</label>
                  <select
                    className="tippi-input"
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                    style={styles.input}
                  >
                    <option value="member">Medlem</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div style={styles.inviteActions}>
                  <button
                    type="submit"
                    className="tippi-btn"
                    style={styles.btn}
                    disabled={inviteLoading}
                  >
                    {inviteLoading ? 'Sender…' : 'Send invitasjon'}
                  </button>
                  <button
                    type="button"
                    className="tippi-btn-secondary"
                    style={styles.btn}
                    onClick={() => { setShowInviteForm(false); setInviteEmail(''); setInviteError(''); }}
                    disabled={inviteLoading}
                  >
                    Avbryt
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Confirm dialog */}
        {confirmDialog && (
          <div style={styles.overlay}>
            <div style={styles.dialog}>
              <p style={styles.dialogLabel}>{confirmDialog.label}</p>
              <div style={styles.dialogActions}>
                <button
                  className="tippi-btn"
                  style={{ ...styles.btn, background: COLORS.danger ?? '#e74c3c' }}
                  onClick={confirmDialog.onConfirm}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Venter…' : 'Bekreft'}
                </button>
                <button
                  className="tippi-btn-secondary"
                  style={styles.btn}
                  onClick={() => setConfirmDialog(null)}
                  disabled={actionLoading}
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  root:         { minHeight: '100vh', background: COLORS.bg ?? '#f5f5f5' },
  page:         { maxWidth: 600, margin: '0 auto', padding: '24px 20px', paddingTop: 'max(28px, env(safe-area-inset-top))', display: 'flex', flexDirection: 'column', gap: 16 },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  title:        { fontFamily: FONTS.display, fontSize: 36, lineHeight: 1.05, fontWeight: 700, margin: 0, color: COLORS.textDark },
  subtitle:     { fontSize: 13, color: COLORS.textLight ?? '#888', margin: '4px 0 0' },
  section:      { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 },
  sectionHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle: { fontFamily: FONTS.display, fontSize: 22, fontWeight: 700, color: COLORS.textDark, margin: 0 },
  memberRow:    { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${COLORS.border ?? '#f0f0f0'}` },
  memberInfo:   { flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  memberName:   { fontSize: 15, fontWeight: 700, color: COLORS.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  memberMeta:   { fontSize: 12, color: COLORS.textLight ?? '#888' },
  memberActions:{ display: 'flex', gap: 8 },
  roleBadge:    { display: 'inline-block', fontSize: 11, fontWeight: 700, color: '#fff', padding: '3px 10px', borderRadius: 20 },
  ownerBadge:   { fontSize: 11, fontWeight: 700, color: COLORS.textLight ?? '#888', padding: '2px 8px' },
  approveBtn:   { fontSize: 13, padding: '7px 16px', whiteSpace: 'nowrap', borderRadius: 26, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontWeight: 700, cursor: 'pointer' },
  rejectBtn:    { fontSize: 13, padding: '7px 16px', whiteSpace: 'nowrap', borderRadius: 26, border: `1.5px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 700, cursor: 'pointer' },
  removeBtn:    { fontSize: 13, padding: '7px 16px', whiteSpace: 'nowrap', borderRadius: 26, border: `1.5px solid ${COLORS.danger}`, background: COLORS.white, color: COLORS.danger, fontWeight: 700, cursor: 'pointer' },
  leaveBtn:     { fontSize: 13, padding: '9px 18px', whiteSpace: 'nowrap', borderRadius: 26, border: `1.5px solid ${COLORS.danger}`, background: COLORS.white, color: COLORS.danger, fontWeight: 700, cursor: 'pointer' },
  inviteOpenBtn:{ alignSelf: 'flex-start', padding: '11px 22px', borderRadius: 26, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontWeight: 700, cursor: 'pointer' },
  inviteForm:   { display: 'flex', flexDirection: 'column', gap: 14 },
  inviteActions:{ display: 'flex', gap: 10 },
  fieldGroup:   { display: 'flex', flexDirection: 'column', gap: 4 },
  label:        { fontSize: 12, fontWeight: 600, color: COLORS.textLight ?? '#888', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input:        { width: '100%', height: 52, padding: '0 14px', fontSize: 15.5, boxSizing: 'border-box', borderRadius: 14, border: `1px solid ${COLORS.border}`, background: COLORS.white },
  btn:          { height: 52, padding: '0 22px', fontSize: 16, borderRadius: 26, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontWeight: 700, cursor: 'pointer' },
  empty:        { fontSize: 14, color: COLORS.textLight ?? '#999', textAlign: 'center', margin: '8px 0' },
  error:        { background: '#fff0f0', border: '1px solid #ffb3b3', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#c0392b' },
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  dialog:       { background: COLORS.white, borderRadius: 22, padding: 24, width: '100%', maxWidth: 360, boxShadow: SHADOWS.md, display: 'flex', flexDirection: 'column', gap: 20 },
  dialogLabel:  { fontFamily: FONTS.display, fontSize: 26, fontWeight: 700, color: COLORS.textDark, margin: 0, textAlign: 'center' },
  dialogActions:{ display: 'flex', flexDirection: 'column', gap: 10 },
  card:         { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 22, padding: 24, display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'center' },
};
